import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { showToast } from "../Buttons/Toaster";

const GitHubUploadPanel = ({
  show,
  setShow,
  jobId,
  fileId = null,
  jobName,
  fileName = null,
  source_db = null,
  targetDb = null,
  onUploadSuccess
}) => {
  const [formData, setFormData] = useState({
    repo_name: "",
    repo_file_path: "",
    commit_message: "",
    branch: ""
  });
  const [branches, setBranches] = useState([]);
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [reposLoading, setReposLoading] = useState(false);
  const [userRepo, setUserRepo] = useState(""); // ADD THIS STATE for storing the user's repository
  
  // Track if we've already fetched data for this session
  const hasFetchedRef = useRef(false);
  // Check if target is Coalesce
  const isCoalesceTarget = targetDb && targetDb.toLowerCase() === "coalesce";

  const apiBaseUrl = process.env.REACT_APP_API_URL_CODE_CONVERSION;

  useEffect(() => {
    if (show && !hasFetchedRef.current) {
      // Fetch data only once when panel opens
      fetchUserRepoAndBranches(); // COMBINED FUNCTION - fetch both repo and branches
      hasFetchedRef.current = true;
      
      // Set default folder path based on target
      const defaultFolderPath = isCoalesceTarget ? "nodes" : jobName;
      
      // Set default commit message with actual source and target values
      const sourceTargetText = source_db && targetDb 
        ? `${source_db} to ${targetDb}` 
        : "source to target";
      const defaultCommitMessage = `Code conversion of ${sourceTargetText} of job ${jobName}`;
      
      setFormData(prev => ({
        ...prev,
        commit_message: defaultCommitMessage,
        repo_file_path: defaultFolderPath
      }));
    }
    
    // Reset the fetch flag when panel closes
    return () => {
      if (!show) {
        hasFetchedRef.current = false;
      }
    };
  }, [show, jobName, source_db, targetDb, isCoalesceTarget]);

  // COMBINED FUNCTION to fetch user repo and branches
  const fetchUserRepoAndBranches = async () => {
    // Prevent multiple simultaneous requests
    if (branchesLoading) return;
    
    setBranchesLoading(true);
    const authToken = sessionStorage.getItem("access_token");

    if (!authToken) {
      showToast("Authentication token not found","error");
      setBranchesLoading(false);
      return;
    }

    try {
      // Fetch user's GitHub configuration
      const configResponse = await axios.get(`${apiBaseUrl}/user_github_details`, {
        headers: {
          "Authorization": `Bearer ${authToken}`
        }
      });

      if (!configResponse.data || !configResponse.data.repo_name || !configResponse.data.username) {
        showToast("GitHub configuration not found. Please set it up in Account Settings first.","error");
        setBranchesLoading(false);
        handleClose();
        return;
      }
      
      const repoName = configResponse.data.repo_name;
      const username = configResponse.data.username;
      
      // Set the user repository in format: username/repo_name
      const fullRepoName = `${username}/${repoName}`;
      setUserRepo(fullRepoName);
      setFormData(prev => ({
        ...prev,
        repo_name: fullRepoName
      }));

      // Fetch branches for the configured repository
      const formDataForApi = new URLSearchParams();
      formDataForApi.append('repo_name', repoName);

      const branchResponse = await axios.post(
        `${apiBaseUrl}/branches`,
        formDataForApi.toString(),
        {
          headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/x-www-form-urlencoded"
          }
        }
      );
      
      if (branchResponse.data.status === "SUCCESS") {
        setBranches(branchResponse.data.branches);
      } else {
        showToast(branchResponse.data.message || "Failed to fetch branches","error");
      }
      
    } catch (error) {
      console.error("Failed to fetch GitHub details:", error);
      showToast("Failed to fetch GitHub configuration","error");
    } finally {
      setBranchesLoading(false);
    }
  };

  // REMOVED: fetchRepos function - no longer needed

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpload = async () => {
    if (!formData.repo_name || !formData.repo_file_path || !formData.commit_message || !formData.branch) {
      showToast("Please fill all required fields","error");
      return;
    }

    try {
      setLoading(true);
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) {
        showToast("Authentication token not found","error");
        setLoading(false);
        return;
    }

      // Prepare form data as x-www-form-urlencoded
      const uploadFormData = new URLSearchParams();
      uploadFormData.append('repo_name', formData.repo_name);
      uploadFormData.append('repo_file_path', formData.repo_file_path);
      uploadFormData.append('commit_message', formData.commit_message);
      uploadFormData.append('branch', formData.branch);
      uploadFormData.append('job_id', jobId.toString());
      
      // Only append file_id if it exists
      if (fileId) {
        uploadFormData.append('file_id', fileId.toString());
      } else {
        uploadFormData.append('file_id', '');
      }

      const response = await axios.post(
        `${apiBaseUrl}/upload`,
        uploadFormData.toString(),
        {
          headers: {
            "Authorization": `Bearer ${authToken}`,
            "Content-Type": "application/x-www-form-urlencoded"
          }
        }
      );

      if (response.data.status === "SUCCESS") {
        showToast("Files uploaded to GitHub successfully!","success");
        onUploadSuccess?.();
        handleClose();
      } else {
        showToast(response.data.message || "Upload failed","error");
      }
    } catch (error) {
      console.error("GitHub upload failed:", error);
      showToast("Failed to upload to GitHub","error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShow(false);
    setFormData({
      repo_name: userRepo, // Keep the repo name when resetting
      repo_file_path: "",
      commit_message: "",
      branch: "main"
    });
    // Reset the fetch flag when manually closing
    hasFetchedRef.current = false;
  };

  const isFormValid = formData.repo_name && formData.repo_file_path && formData.commit_message && formData.branch;

  if (!show) return null;

  return (
    <div>
      <div className={`right-bar3 p-3 ${show ? "d-block" : "d-none"}`}>
        <div>
          <div className="d-flex align-items-center justify-content-between">
            <h6 className="font-size-13 mb-0 fw-medium text-dark">
              Upload to GitHub Repository
            </h6>
            <i
              onClick={handleClose}
              className="mdi mdi-close close-trace cursor-pointer right-bar-toggle3 iconColor"
            />
          </div>
          
          <div className="trace-label mt-1 pe-3 d-flex align-items-center mb-3 w-fit-content">
            <img
              src="/assets/images/trace_label.png"
              className="w-14px me-2"
              alt="GitHub icon"
            />
            <h6 className="font-size-10 mb-0 text-dark">
              GitHub Upload – Upload files to repository
            </h6>
          </div>
          
          <div className="mb-3">
            <div className="d-flex flex-wrap" style={{ gap: "20px" }}>
              <div className="flex-grow-1" style={{ Width: "200px" }}>
                <h6 className="fw-sbold font-size-12 mb-2">Job Name</h6>
                <p className="font-size-12 text-dark mb-0">
                  {jobName || "N/A"}
                </p>
              </div>
              {fileName && (
                <div className="flex-grow-1" style={{ minWidth: "200px" }}>
                  <h6 className="fw-sbold font-size-12 mb-2">File Name</h6>
                  <p className="font-size-12 text-dark mb-0">{fileName}</p>
                </div>
              )}
            </div>
          </div>

          <div id="githubUploadContent">
          
            {/* Folder Path Field */}
            <div className="mb-3">
              <h6 className="fw-sbold font-size-12 mb-2">
                Folder Path <span style={{ color: "red" }}>*</span>
              </h6>
              <input
                type="text"
                className="form-control highlighted-input font-size-12 doc-search mt-2 mb-3 colorfor-placeholder styled-input"
                value={formData.repo_file_path}
                onChange={(e) => {
                  if (!isCoalesceTarget) {
                    handleInputChange("repo_file_path", e.target.value);
                  }
                }}
                placeholder="Enter folder path"
                style={{ 
                  width: "100%",
                  backgroundColor: isCoalesceTarget ? "#f8faf8ff" : "white",
                  cursor: isCoalesceTarget ? "not-allowed" : "text"
                }}
                readOnly={isCoalesceTarget}
              />
              {isCoalesceTarget && (
                <div className="text-muted font-size-10 mt-1">
                  <i className="fas fa-info-circle me-1"></i>
                  Folder path is fixed to "nodes" for Coalesce targets.
                </div>
              )}
            </div>

            {/* Repository Field - CHANGED TO READ-Only INPUT */}
            <div className="mb-3">
              <h6 className="fw-sbold font-size-12 mb-2">
                Repository <span style={{ color: "red" }}>*</span>
              </h6>
              <input
                type="text"
                className="form-control highlighted-input font-size-12 doc-search mt-2 mb-3 colorfor-placeholder styled-input"
                value={formData.repo_name}
                readOnly
                placeholder="Loading repository..."
                style={{ 
                  width: "100%",
                  backgroundColor: "#f8f9fa",
                  cursor: "not-allowed"
                }}
              />
              {!formData.repo_name && (
                <div className="text-muted font-size-10 mt-1">
                  <i className="fas fa-info-circle me-1"></i>
                  Repository is automatically set from your GitHub configuration.
                </div>
              )}
            </div>

            {/* Branch Field */}
            <div className="mb-3">
              <h6 className="fw-sbold font-size-12 mb-2">
                Branch <span style={{ color: "red" }}>*</span>
              </h6>
              <select
                className="form-control highlighted-input font-size-12 doc-search mt-2 mb-3 colorfor-placeholder styled-input"
                value={formData.branch}
                onChange={(e) => handleInputChange("branch", e.target.value)}
                disabled={branchesLoading}
                style={{
                  borderRadius: "4px",
                  padding: "8px 12px",
                  fontSize: "12px",
                  width: "100%",
                  appearance: "none",
                  backgroundImage:
                    "url(\"data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 8px center",
                  backgroundSize: "12px",
                }}
              >
                <option value="">Select a branch</option>
                {branches.map((branch) => (
                  <option key={branch} value={branch}>
                    {branch}
                  </option>
                ))}
              </select>
            </div>

            {/* Commit Message Field */}
            <div className="mb-3">
              <h6 className="fw-sbold font-size-12 mb-2">
                Commit Message <span style={{ color: "red" }}>*</span>
              </h6>
              <textarea
                className="form-control highlighted-input font-size-12 doc-search mt-2 mb-3 colorfor-placeholder styled-input"
                placeholder="Describe your changes..."
                rows="3"
                value={formData.commit_message}
                onChange={(e) => handleInputChange("commit_message", e.target.value)}
                style={{ width: "100%", resize: "vertical", height: "auto" }}
              />
            </div>

            <div className="info-text font-size-10 text-muted mb-3">
              <i className="fas fa-info-circle me-1"></i>
              Files will be committed to the selected branch in the GitHub repository.
            </div>
          </div>

          <div className="position-absolute close-tab">
            <div
              className="d-flex justify-content-between align-items-center w-100"
              style={{ marginBottom: "10px" }}
            >
              <button
                onClick={handleClose}
                className="btn btn-closebtn custom-button"
              >
                Close
              </button>

              <button
                className="btn btn-primary custom-button"
                onClick={handleUpload}
                disabled={loading || !isFormValid}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Uploading...
                  </>
                ) : (
                  "Upload to GitHub"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`rightbar-overlay ${show ? "d-block" : "d-none"}`}></div>
    </div>
  );
};

export default GitHubUploadPanel;